"use client";

import { useAuthDialog } from "./AuthContext";
import type { DialogChoice } from "./AuthContext";
import { Modal } from "@/components/ui/modals/Modal";
import { Surface } from "@/components/ui/Surface";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getLabel } from "@/lib/labels/getLabel";

interface DialogOption {
    choice: DialogChoice;
    labelKey: string;
    hintKey: string;
}

const SWITCH_FROM_GUEST_OPTIONS: DialogOption[] = [
    {
        choice: "sign-in-existing",
        labelKey: "auth.anonymous.switchFromGuest.signIn",
        hintKey: "auth.anonymous.switchFromGuest.signInHint",
    },
    {
        choice: "keep-guest",
        labelKey: "auth.anonymous.switchFromGuest.keepGuest",
        hintKey: "auth.anonymous.switchFromGuest.keepGuestHint",
    },
];

// collision-email and collision-google share the same 3-option shape
const COLLISION_OPTIONS: DialogOption[] = [
    {
        choice: "sign-in-existing",
        labelKey: "auth.anonymous.collision.signInToExisting",
        hintKey: "auth.anonymous.collision.signInToExistingHint",
    },
    {
        choice: "use-different-email",
        labelKey: "auth.anonymous.collision.useDifferentEmail",
        hintKey: "auth.anonymous.collision.useDifferentEmailHint",
    },
    {
        choice: "keep-guest",
        labelKey: "auth.anonymous.collision.keepGuest",
        hintKey: "auth.anonymous.collision.keepGuestHint",
    },
];

export function CollisionDialog() {
    const { request, choose } = useAuthDialog();
    const labels = useLabels();

    const isSwitchFromGuest = request?.kind === "switch-from-guest";
    const prefix = isSwitchFromGuest ? "auth.anonymous.switchFromGuest" : "auth.anonymous.collision";
    const options = isSwitchFromGuest ? SWITCH_FROM_GUEST_OPTIONS : COLLISION_OPTIONS;

    // Dismissing (X, backdrop, ESC) is the no-op choice: the journey awaiting the dialog resolves
    // and the user stays exactly where they were.
    return (
        <Modal isOpen={request !== null} onClose={() => choose("keep-guest")}>
            <Surface slot="modal" className="w-[90vw] max-w-md overflow-hidden">
                <div className="px-6 pt-8 pb-4 text-center">
                    <h2 className="text-xl font-bold">{getLabel(labels, `${prefix}.title`)}</h2>
                    <p className="mt-2 text-sm opacity-85">{getLabel(labels, `${prefix}.subtitle`)}</p>
                </div>

                <div className="px-6 pb-6 space-y-3">
                    {options.map((option, index) => (
                        <button
                            key={option.choice}
                            type="button"
                            onClick={() => choose(option.choice)}
                            className={`${index === 0 ? "btn-primary" : "btn-plain"} block w-full py-3 px-4 text-center cursor-pointer`}
                        >
                            <span className="block text-sm font-semibold">{getLabel(labels, option.labelKey)}</span>
                            <span className="block text-xs opacity-80">{getLabel(labels, option.hintKey)}</span>
                        </button>
                    ))}
                </div>
            </Surface>
        </Modal>
    );
}
